import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Star, ChevronDown, Plus, Check, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useFavorites } from '../../context/FavoritesContext'
import { formatApiError } from '../../utils/formatApiError'

const SIZES = {
  sm: 16,
  md: 20,
}

const DROPDOWN_WIDTH = 240
const DROPDOWN_MAX_HEIGHT = 300

/**
 * Звезда «в избранное».
 *
 * Клик по звезде — список по умолчанию в один клик; если талант уже лежит
 * в именованном списке, вместо изменения открывается выпадашка (см. handleToggle).
 * Шеврон открывает её всегда: чекбоксы всех своих списков, создание нового
 * на месте и «Remove from all lists» с отменой.
 *
 * Компонент сам подписан на FavoritesContext — поэтому его можно рисовать
 * в каждой строке таблицы, не добавляя состояние избранного в зависимости
 * useMemo колонок (иначе каждый клик пересобирал бы всю таблицу).
 *
 * Выпадашка рендерится через портал: внутри таблицы с горизонтальной
 * прокруткой и закреплённой колонкой абсолютно спозиционированный блок
 * обрезался бы контейнером. Тот же приём используют SingleSelect/MultiSelect.
 */
const FavoriteStar = ({
  talentId,
  size = 'sm',
  withDropdown = true,
  // 'icon' — компактная звезда для строки таблицы;
  // 'button' — подписанная кнопка в один ряд с Edit / Merge / Delete
  variant = 'icon',
  className = '',
}) => {
  const isButton = variant === 'button'
  const { lists, loaded, listIdsOf, isPending, toggleFavorite, setListsFor, createList } = useFavorites()
  const [open, setOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState({})
  const [newListName, setNewListName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const wrapRef = useRef(null)
  const dropdownRef = useRef(null)

  const listIds = listIdsOf(talentId)
  const favorited = (listIds?.size || 0) > 0
  const pending = isPending(talentId)
  const iconSize = SIZES[size] || SIZES.sm

  const defaultListId = lists.find(list => list.is_default)?.id ?? null
  // -1 — временная метка оптимистичного обновления, когда дефолтного списка ещё нет
  const memberships = listIds ? [...listIds].filter(id => id > 0) : []
  const inOtherLists = memberships.some(id => id !== defaultListId)

  const updatePosition = useCallback(() => {
    if (!wrapRef.current) return
    const rect = wrapRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const openUpward = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow
    // прижимаем к правому краю кнопки, но не даём уехать за окно
    const left = Math.max(8, Math.min(rect.right - DROPDOWN_WIDTH, window.innerWidth - DROPDOWN_WIDTH - 8))

    setDropdownStyle({
      position: 'fixed',
      left,
      width: DROPDOWN_WIDTH,
      zIndex: 9999,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4, maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, spaceAbove - 8) }
        : { top: rect.bottom + 4, maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, spaceBelow - 8) }
      ),
    })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePosition()
    const onMouseDown = (event) => {
      const insideTrigger = wrapRef.current?.contains(event.target)
      const insideDropdown = dropdownRef.current?.contains(event.target)
      if (!insideTrigger && !insideDropdown) {
        setOpen(false)
        setNewListName('')
        setCreateError('')
      }
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { setOpen(false); setNewListName(''); setCreateError('') }
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, updatePosition])

  const listNames = lists
    .filter(list => listIds?.has(list.id))
    .map(list => list.name)

  const title = (() => {
    if (!favorited) return 'Add to favorites'
    const where = listNames.length > 0 ? `In favorites: ${listNames.join(', ')}` : 'In favorites'
    if (inOtherLists && withDropdown) return `${where}. Click to choose lists`
    return `${where}. Click to remove`
  })()

  /**
   * Клик по звезде переключает только список по умолчанию — из именованных
   * списков он таланта не выкидывает. Но пока талант лежит в именованном
   * списке, звезда остаётся закрашенной до и после клика, и промах по шеврону
   * менял бы данные незаметно. Поэтому в этом случае звезда не меняет ничего,
   * а показывает ту же выпадашку, что и шеврон.
   */
  const handleToggle = (event) => {
    event.stopPropagation()
    event.preventDefault()
    if (pending) return
    if (inOtherLists && withDropdown) {
      setOpen(true)
      return
    }
    toggleFavorite(talentId)
  }

  const handleToggleList = (event, listId) => {
    event.stopPropagation()
    const current = listIds ? [...listIds] : []
    const next = current.includes(listId)
      ? current.filter(id => id !== listId)
      : [...current, listId]
    setListsFor(talentId, next)
  }

  /**
   * Единственное место, где талант уходит сразу из всех списков.
   * Действие явное и подписанное, но снести можно много — поэтому тост
   * с отменой: прежний набор списков известен, вернуть его — один setListsFor.
   */
  const handleRemoveAll = async (event) => {
    event.stopPropagation()
    const previous = [...memberships]
    if (previous.length === 0) return
    setOpen(false)
    setNewListName('')

    // Тост показываем только после подтверждения сервером: иначе «Undo»
    // предлагался бы и там, где удаление вообще не прошло.
    const removed = await setListsFor(talentId, [])
    if (!removed) return

    toast((t) => (
      <span className="flex items-center gap-3">
        Removed from {previous.length === 1 ? '1 list' : `${previous.length} lists`}
        <button
          type="button"
          onClick={async () => {
            toast.dismiss(t.id)
            // Обязательно ждём ответ: молчаливый откат оставил бы звезду
            // горящей при пустом избранном на сервере.
            const restored = await setListsFor(talentId, previous)
            if (restored) toast.success('Favorites restored')
          }}
          className="font-medium text-primary-600 hover:underline"
        >
          Undo
        </button>
      </span>
    ), { duration: 8000 })
  }

  const handleCreateList = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    const name = newListName.trim()
    if (!name || creating) return
    setCreating(true)
    setCreateError('')
    try {
      const created = await createList(name)
      if (created?.id) {
        await setListsFor(talentId, [...memberships, created.id])
      }
      setNewListName('')
    } catch (error) {
      // Дубликат имени (409) — самый частый отказ, показываем прямо у поля
      const message = formatApiError(error, 'Failed to create list')
      setCreateError(message)
      toast.error(message)
    } finally {
      setCreating(false)
    }
  }

  const openDropdown = (event) => {
    event.stopPropagation()
    setOpen(prev => !prev)
  }

  return (
    <div
      ref={wrapRef}
      className={`relative inline-flex items-center ${isButton ? '' : 'gap-1'} ${className}`}
    >
      {isButton ? (
        // Рядом с Edit / Merge / Delete голая иконка читалась бы как украшение,
        // а не как контрол, — поэтому здесь подписанная кнопка с рамкой той же
        // высоты (py-1.5), что у соседей. Заливку не берём: жёлтый уже занят Merge,
        // цветом состояния остаётся только сама звезда.
        <div className="inline-flex items-stretch rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-tertiary overflow-hidden">
          <button
            type="button"
            onClick={handleToggle}
            disabled={pending}
            title={title}
            aria-label={title}
            aria-pressed={favorited}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50"
          >
            <Star
              size={16}
              className={favorited ? 'text-amber-500' : 'text-light-text-tertiary dark:text-dark-text-tertiary'}
              fill={favorited ? 'currentColor' : 'none'}
            />
            {favorited ? 'In favorites' : 'Favorite'}
          </button>

          {withDropdown && (
            <>
              <span className="w-px self-stretch bg-light-border-primary dark:bg-dark-border-primary" />
              <button
                type="button"
                onClick={openDropdown}
                title="Add to a specific list"
                aria-label="Choose favorite lists"
                aria-expanded={open}
                className="inline-flex items-center px-2 text-light-text-tertiary dark:text-dark-text-tertiary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
              >
                <ChevronDown size={14} />
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={handleToggle}
            disabled={pending}
            title={title}
            aria-label={title}
            aria-pressed={favorited}
            className={`p-1 rounded transition-colors disabled:opacity-50 ${
              favorited
                ? 'text-amber-500 hover:text-amber-600'
                : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-amber-500'
            }`}
          >
            <Star size={iconSize} fill={favorited ? 'currentColor' : 'none'} />
          </button>

          {withDropdown && (
            <button
              type="button"
              onClick={openDropdown}
              title="Add to a specific list"
              aria-label="Choose favorite lists"
              aria-expanded={open}
              className={`p-1 rounded transition-colors ${
                open
                  ? 'text-light-text-primary dark:text-dark-text-primary'
                  : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
              }`}
            >
              <ChevronDown size={14} />
            </button>
          )}
        </>
      )}

      {withDropdown && open && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          onClick={(event) => event.stopPropagation()}
          className="flex flex-col rounded-lg border border-light-border-primary dark:border-dark-border-primary bg-light-bg-primary dark:bg-dark-bg-secondary shadow-lg py-1"
        >
          <div className="px-3 py-1.5 text-xs font-medium text-light-text-tertiary dark:text-dark-text-tertiary">
            Add to list
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-light">
            {!loaded && (
              <div className="px-3 py-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                Loading…
              </div>
            )}
            {loaded && lists.length === 0 && (
              <div className="px-3 py-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                No lists yet — create the first one below.
              </div>
            )}

            {lists.map(list => {
              const checked = listIds?.has(list.id) || false
              return (
                <button
                  key={list.id}
                  type="button"
                  onClick={(event) => handleToggleList(event, list.id)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-tertiary"
                >
                  <span className={`w-4 h-4 shrink-0 flex items-center justify-center rounded border ${
                    checked
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'border-light-border-primary dark:border-dark-border-primary'
                  }`}>
                    {checked && <Check size={12} />}
                  </span>
                  <span className="truncate">{list.name}</span>
                  {list.is_default && (
                    <span className="ml-auto shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">default</span>
                  )}
                </button>
              )
            })}
          </div>

          <form onSubmit={handleCreateList} className="px-3 py-2 border-t border-light-border-primary dark:border-dark-border-primary mt-1">
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={newListName}
                onChange={(event) => { setNewListName(event.target.value); if (createError) setCreateError('') }}
                placeholder="New list..."
                maxLength={120}
                aria-invalid={!!createError}
                className={`flex-1 min-w-0 px-2 py-1 text-sm rounded border bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary ${
                  createError
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-light-border-primary dark:border-dark-border-primary'
                }`}
              />
              <button
                type="submit"
                disabled={!newListName.trim() || creating}
                title="Create list and add the talent to it"
                className="p-1 rounded text-primary-600 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-tertiary disabled:opacity-40"
              >
                <Plus size={16} />
              </button>
            </div>
            {createError && (
              <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
                {createError}
              </p>
            )}
          </form>

          {favorited && (
            <button
              type="button"
              onClick={handleRemoveAll}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 border-t border-light-border-primary dark:border-dark-border-primary hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 size={14} className="shrink-0" />
              Remove from all lists
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

export default FavoriteStar
